import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, LogOut } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/components/ui/Card";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DepartureFormProps {
  personId: string;
  personName: string;
  hasOpenBedAssignment?: boolean;
  onComplete?: () => void;
}

interface DropdownItem {
  id: number;
  itemName: string;
}

interface CustomFieldDefinition {
  id: number;
  fieldLabel: string;
  fieldType: string;
  choices?: { id: number; choiceLabel: string }[];
}

interface DeparturePayload {
  exitDate: string;
  destination?: string;
  reasonForLeaving?: string;
  outcomeNotes?: string;
  hideSensitiveData?: boolean;
  customFieldValues?: Record<string, string | string[]>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DepartureForm({
  personId,
  personName,
  hasOpenBedAssignment = false,
  onComplete,
}: DepartureFormProps) {
  const queryClient = useQueryClient();

  const [exitDate, setExitDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [destination, setDestination] = useState("");
  const [reasonForLeaving, setReasonForLeaving] = useState("");
  const [outcomeNotes, setOutcomeNotes] = useState("");
  const [hideSensitiveData, setHideSensitiveData] = useState(false);
  const [customFieldValues, setCustomFieldValues] = useState<
    Record<string, string | string[]>
  >({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch dropdown options for destination and reason
  const { data: destinations } = useQuery<DropdownItem[]>({
    queryKey: ["dropdown", "exit_destination"],
    queryFn: async () => {
      try {
        const res = await api.get<{ data: DropdownItem[] }>(
          "/config/dropdowns/exit_destination",
        );
        return res.data;
      } catch {
        return [];
      }
    },
  });

  const { data: reasons } = useQuery<DropdownItem[]>({
    queryKey: ["dropdown", "exit_reason"],
    queryFn: async () => {
      try {
        const res = await api.get<{ data: DropdownItem[] }>(
          "/config/dropdowns/exit_reason",
        );
        return res.data;
      } catch {
        return [];
      }
    },
  });

  // Fetch configurable departure questions
  const { data: departureFields } = useQuery<CustomFieldDefinition[]>({
    queryKey: ["customFields", "depart"],
    queryFn: async () => {
      try {
        const res = await api.get<{ data: CustomFieldDefinition[] }>(
          "/config/custom-fields/depart",
        );
        return res.data;
      } catch {
        return [];
      }
    },
  });

  const departureMutation = useMutation({
    mutationFn: async (payload: DeparturePayload) => {
      return api.post(`/clients/${personId}/departure`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client", personId] });
      queryClient.invalidateQueries({
        queryKey: ["departure", personId],
      });
      onComplete?.();
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!exitDate) {
      newErrors.exitDate = "Exit date is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setShowConfirmation(true);
  };

  const handleConfirm = () => {
    setShowConfirmation(false);
    departureMutation.mutate({
      exitDate: exitDate || '',
      destination: destination || undefined,
      reasonForLeaving: reasonForLeaving || undefined,
      outcomeNotes: outcomeNotes || undefined,
      hideSensitiveData,
      customFieldValues:
        Object.keys(customFieldValues).length > 0
          ? customFieldValues
          : undefined,
    });
  };

  const handleCustomFieldChange = (
    fieldId: number,
    value: string | string[],
  ) => {
    setCustomFieldValues((prev) => ({
      ...prev,
      [String(fieldId)]: value,
    }));
  };

  const handleMultiChoiceToggle = (fieldId: number, choiceId: string) => {
    setCustomFieldValues((prev) => {
      const current = (prev[String(fieldId)] as string[]) || [];
      const updated = current.includes(choiceId)
        ? current.filter((id) => id !== choiceId)
        : [...current, choiceId];
      return { ...prev, [String(fieldId)]: updated };
    });
  };

  // Filter out the standard fields from custom fields display
  const displayableCustomFields = (departureFields ?? []).filter(
    (f) =>
      f.fieldLabel !== "destination" &&
      f.fieldLabel !== "reason_for_leaving" &&
      f.fieldLabel !== "outcome_notes",
  );

  return (
    <div className="space-y-6">
      {/* Bed assignment warning */}
      {hasOpenBedAssignment && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-medium">Open Bed Assignment</p>
            <p className="text-sm">
              This client has an active bed assignment that will be
              automatically closed when the departure is recorded.
            </p>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LogOut className="h-5 w-5" />
            Record Departure
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Exit Date */}
          <Input
            label="Exit Date"
            type="date"
            required
            value={exitDate}
            onChange={(e) => setExitDate(e.target.value)}
            error={errors.exitDate}
          />

          {/* Destination */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium leading-none text-foreground">
              Destination
            </label>
            <select
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">-- Select destination --</option>
              {(destinations ?? []).map((item) => (
                <option key={item.id} value={item.itemName}>
                  {item.itemName}
                </option>
              ))}
            </select>
          </div>

          {/* Reason for leaving */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium leading-none text-foreground">
              Reason for Leaving
            </label>
            <select
              value={reasonForLeaving}
              onChange={(e) => setReasonForLeaving(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">-- Select reason --</option>
              {(reasons ?? []).map((item) => (
                <option key={item.id} value={item.itemName}>
                  {item.itemName}
                </option>
              ))}
            </select>
          </div>

          {/* Outcome notes */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium leading-none text-foreground">
              Exit Outcome Notes
            </label>
            <textarea
              value={outcomeNotes}
              onChange={(e) => setOutcomeNotes(e.target.value)}
              rows={3}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="Notes about the exit outcome..."
            />
          </div>

          {/* Configurable departure questions */}
          {displayableCustomFields.length > 0 && (
            <div className="space-y-4 border-t pt-4">
              <h4 className="text-sm font-semibold text-foreground">
                Departure Questions
              </h4>
              {displayableCustomFields.map((field) => (
                <div key={field.id} className="space-y-1.5">
                  <label className="text-sm font-medium leading-none text-foreground">
                    {field.fieldLabel}
                  </label>

                  {field.fieldType === "text" && (
                    <input
                      type="text"
                      value={
                        (customFieldValues[String(field.id)] as string) ?? ""
                      }
                      onChange={(e) =>
                        handleCustomFieldChange(field.id, e.target.value)
                      }
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  )}

                  {field.fieldType === "select" && field.choices && (
                    <select
                      value={
                        (customFieldValues[String(field.id)] as string) ?? ""
                      }
                      onChange={(e) =>
                        handleCustomFieldChange(field.id, e.target.value)
                      }
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <option value="">-- Select --</option>
                      {field.choices.map((choice) => (
                        <option
                          key={choice.id}
                          value={String(choice.id)}
                        >
                          {choice.choiceLabel}
                        </option>
                      ))}
                    </select>
                  )}

                  {field.fieldType === "multi_select" && field.choices && (
                    <div className="space-y-1">
                      {field.choices.map((choice) => {
                        const selected =
                          (
                            (customFieldValues[
                              String(field.id)
                            ] as string[]) ?? []
                          ).includes(String(choice.id));
                        return (
                          <label
                            key={choice.id}
                            className="flex items-center gap-2 text-sm"
                          >
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() =>
                                handleMultiChoiceToggle(
                                  field.id,
                                  String(choice.id),
                                )
                              }
                              className="rounded border-input"
                            />
                            {choice.choiceLabel}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Hide sensitive data option */}
          <div className="border-t pt-4">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={hideSensitiveData}
                onChange={(e) => setHideSensitiveData(e.target.checked)}
                className="mt-0.5 rounded border-input"
              />
              <div>
                <span className="text-sm font-medium text-foreground">
                  Hide sensitive data on exit
                </span>
                <p className="text-xs text-muted-foreground">
                  Clears chemical dependence, medications, and mental health
                  fields from the client record.
                </p>
              </div>
            </label>
          </div>
        </CardContent>

        <CardFooter className="justify-end gap-2">
          <Button
            variant="outline"
            onClick={onComplete}
            disabled={departureMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            loading={departureMutation.isPending}
            className="gap-2"
          >
            <LogOut className="h-4 w-4" />
            Complete Departure
          </Button>
        </CardFooter>
      </Card>

      {/* Confirmation dialog */}
      {showConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="mx-4 max-w-md">
            <CardHeader>
              <CardTitle className="text-lg">Confirm Departure</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to record the departure for{" "}
                <span className="font-medium text-foreground">
                  {personName}
                </span>
                ?
              </p>
              {hasOpenBedAssignment && (
                <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                  The open bed assignment will be automatically closed.
                </p>
              )}
              {hideSensitiveData && (
                <p className="mt-2 text-sm text-destructive">
                  Sensitive data (chemical dependence, medications, mental
                  health) will be permanently removed.
                </p>
              )}
            </CardContent>
            <CardFooter className="justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowConfirmation(false)}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleConfirm}>
                Confirm Departure
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}

      {/* Error display */}
      {departureMutation.isError && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          Failed to record departure. Please try again.
        </div>
      )}
    </div>
  );
}
